/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../storeSetup';
import {
  getHomeDepotMemberRelation,
  getLennoxMemberRelation,
  getPaymentMethods, getSelectedPartnersForMember,
  MemberPartnerRelation, setLennoxMemberKey, setPaymentMethod,
  updateMemberToPartner, updateCompanyPartner, getYearlyPayments,
  getMemberConfig,
  getHDSupplyMemberRelation,
  verifyAndUpdatePaypalEmail,
  CompanyPartnerRelation,
  getCompanyPartnerRelations,
  unlinkPaymentMethod,
  getCompanyWallet,
  CompanyWallet,
  CompanyRewardsWallet,
  CompanyPayoutsWallet,
  updateCompanyPartnerRelation,
  CompanyPartnerOptin,
  getCompanyPartnerOptin,
  createCompanyPartnerOptin,
  getCompanyRewardsWallet,
  getCompanyPayoutsWallet,
  activatePaymentMethod,
  getAllActivePartners,
  getPartnerProfileConfig,
  getAllCategories,
  getPartnersByCategories,
  getRecentRebatePartners,
  enrollCompanyToPartner,
  unlinkCompanyFromPartner,
} from '../thunks/dashboardThunk';
import { Category } from '../../common/models/Category';
import { MemberConfig } from '../../common/models/Member';
import { HDSetupFlowSteps, HDSupplyEnrollmentStatus } from '../../common/enums';
import {
  BankAccount, PaperCheque, PaymentMethod, PaymentMethodType, PayPalAccount,
} from '../../common/models/PaymentMethod';
import { Partner } from '../../common/models/Partner';
import { PartnerProfileConfig } from '../../common/models/PartnerProfile';

interface DashboardState {
  payPalAccount: PayPalAccount | undefined;
  bankAccount: BankAccount | undefined;
  paperCheque: PaperCheque | undefined;
  paymentMethods: PaymentMethod[];
  getPaymentMethodState: 'idle' | 'pending';
  setPayPalAccountIdState: 'idle' | 'pending';
  setPaymentMethodstate: 'idle' | 'pending';
  activatePaymentMethodstate: 'idle' | 'pending';
  lennoxMemberRelation: MemberPartnerRelation | undefined;
  homeDepotMemberRelation: MemberPartnerRelation | undefined;
  selectedPartners: MemberPartnerRelation[];
  getLennoxMemberRelationState: 'idle' | 'pending';
  getHomeDepotMemberRelationState: 'idle' | 'pending';
  setLennoxMemberKeyState: 'idle' | 'pending';
  getSelectedPartnersForMemberState: 'idle' | 'pending';
  getYearlyPaymentsState: 'idle' | 'pending';
  myAccountRenderIndex: {
    value: number;
    descr: string;
  }
  updateMemberToPartnerState: 'idle' | 'pending';
  updateCompanyPartnerState: 'idle' | 'pending';
  HDSetupFlowStepsCompletionState: HDSetupFlowSteps;
  yearlyPayments: { year: number, total: string }[];
  getMemberConfigState: 'idle' | 'pending';
  memberConfig: MemberConfig | undefined;
  HDSupplyEnrollmentState: HDSupplyEnrollmentStatus;
  hdSupplyMemberRelation: MemberPartnerRelation | undefined;
  getHDSupplyMemberRelationState: 'idle' | 'pending';
  memberPaypalAuthorizationToken: string | null;
  companyPartnerRelations: CompanyPartnerRelation[] | undefined;
  getCompanyPartnerRelationsState: 'idle' | 'pending';
  categories: Category[] | undefined;
  getCategoriesState: 'idle' | 'pending';
  showDashboardToolbar: boolean;
  companyWallet: CompanyWallet | undefined;
  companyRewardsWallet: CompanyRewardsWallet;
  companyPayoutsWallet: CompanyPayoutsWallet;
  getCompanyWalletState: 'idle' | 'pending';
  getCompanyRewardsWalletState: 'idle' | 'pending';
  getCompanyPayoutsWalletState: 'idle' | 'pending';
  unlinkPaypalState: 'idle' | 'pending';
  unlinkPaymentMethodState: 'idle' | 'pending';
  payPalSetAsDefault: boolean;
  checklistCompletedSteps: string[];
  checklistAllStepsCompleted: boolean;
  onboardingChecklistViewCount: number;
  checklistCompletedStateShown: boolean;
  // Partner ids the member has already submitted a "Contact sales" request for. Persisted with
  // the rest of the dashboard slice so the CTA stays disabled across reloads (SN-874).
  contactedSalesPartnerIds: string[];
  allActivePartners: Partner[] | undefined;
  getAllActivePartnersState: 'idle' | 'pending';
  recentRebatePartners: Partner[] | undefined;
  getRecentRebatePartnersState: 'idle' | 'pending';
  partnersByCategories: Partner[] | undefined;
  getPartnersByCategoriesState: 'idle' | 'pending';
  getPartnerProfileConfigState: 'idle' | 'pending';
  partnerProfileConfig: PartnerProfileConfig;
  updateCompanyPartnerRelationState: 'idle' | 'pending';
  companyPartnerOptin: CompanyPartnerOptin | undefined;
  getCompanyPartnerOptinState: 'idle' | 'pending' | 'error';
  createCompanyPartnerOptinState: 'idle' | 'pending' | 'error';
}

// Define the initial state using that type
const initialState: DashboardState = {
  payPalAccount: undefined,
  bankAccount: undefined,
  paperCheque: undefined,
  paymentMethods: [],
  selectedPartners: [],
  getPaymentMethodState: 'pending',
  setPayPalAccountIdState: 'idle',
  setPaymentMethodstate: 'idle',
  activatePaymentMethodstate: 'idle',
  lennoxMemberRelation: undefined,
  homeDepotMemberRelation: undefined,
  getLennoxMemberRelationState: 'pending',
  getHomeDepotMemberRelationState: 'pending',
  setLennoxMemberKeyState: 'idle',
  getSelectedPartnersForMemberState: 'pending',
  getYearlyPaymentsState: 'idle',
  myAccountRenderIndex: {
    value: 0,
    descr: 'Active tab in my account page',
  },
  updateMemberToPartnerState: 'idle',
  updateCompanyPartnerState: 'idle',
  HDSetupFlowStepsCompletionState: HDSetupFlowSteps.None,
  yearlyPayments: [],
  getMemberConfigState: 'pending',
  memberConfig: undefined,
  HDSupplyEnrollmentState: HDSupplyEnrollmentStatus.Pending,
  hdSupplyMemberRelation: undefined,
  getHDSupplyMemberRelationState: 'pending',
  memberPaypalAuthorizationToken: null,
  companyPartnerRelations: undefined,
  getCompanyPartnerRelationsState: 'idle',
  categories: undefined,
  getCategoriesState: 'idle',
  showDashboardToolbar: false,
  companyWallet: undefined,
  companyRewardsWallet: {
    balance: '0',
    totalRewards: 0,
    transactions: [],
  },
  companyPayoutsWallet: {
    totalPayouts: 0,
    transactions: [],
  },
  getCompanyWalletState: 'idle',
  getCompanyRewardsWalletState: 'idle',
  getCompanyPayoutsWalletState: 'idle',
  unlinkPaypalState: 'idle',
  unlinkPaymentMethodState: 'idle',
  payPalSetAsDefault: false,
  checklistCompletedSteps: [],
  checklistAllStepsCompleted: false,
  onboardingChecklistViewCount: 0,
  checklistCompletedStateShown: false,
  contactedSalesPartnerIds: [],
  allActivePartners: undefined,
  getAllActivePartnersState: 'idle',
  recentRebatePartners: undefined,
  getRecentRebatePartnersState: 'pending',
  partnersByCategories: undefined,
  getPartnersByCategoriesState: 'idle',
  partnerProfileConfig: {} as PartnerProfileConfig,
  getPartnerProfileConfigState: 'idle',
  updateCompanyPartnerRelationState: 'idle',
  companyPartnerOptin: undefined,
  getCompanyPartnerOptinState: 'idle',
  createCompanyPartnerOptinState: 'idle',
};

const onboardingChecklistTotalSteps = 3; // Total steps in the onboarding checklist

export const DashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setMyAccountRenderIndex: (state, action: PayloadAction<number>) => {
      state.myAccountRenderIndex.value = action.payload;
    },
    UpdateHDSetupFlowStepsCompletionState: (state, action: PayloadAction<HDSetupFlowSteps>) => {
      state.HDSetupFlowStepsCompletionState = action.payload;
    },
    resetHomeDepotMemberRelation: (state) => {
      state.homeDepotMemberRelation = undefined;
    },
    resetCompanyPartnerOptin: (state) => {
      state.companyPartnerOptin = undefined;
      state.getCompanyPartnerOptinState = 'idle';
      state.createCompanyPartnerOptinState = 'idle';
    },
    UpdateHDSupplyEnrollmentState: (state, action: PayloadAction<HDSupplyEnrollmentStatus>) => {
      state.HDSupplyEnrollmentState = action.payload;
    },
    UpdateMemberPaypalAuthorizationToken: (state, action: PayloadAction<string>) => {
      state.memberPaypalAuthorizationToken = action.payload;
    },
    UpdateShowDashboardToolbar: (state, action: PayloadAction<boolean>) => {
      state.showDashboardToolbar = action.payload;
    },
    UpdateBankAccountState: (state, action: PayloadAction<BankAccount>) => {
      if (action.payload?.type === PaymentMethodType.BANK_ACCOUNT) {
        state.bankAccount = action.payload;
      }
    },
    UpdatePaperChequeState: (state, action: PayloadAction<PaperCheque>) => {
      if (action.payload?.type === PaymentMethodType.PAPER_CHEQUE) {
        state.paperCheque = action.payload;
      }
    },
    UpdatePayPalSetAsDefault: (state, action: PayloadAction<boolean>) => {
      state.payPalSetAsDefault = action.payload;
    },
    UpdateChecklistCompletedSteps: (state, action: PayloadAction<string>) => {
      if (!state.checklistCompletedSteps.includes(action.payload)) {
        state.checklistCompletedSteps.push(action.payload);
      }
      if (state.checklistCompletedSteps.length === onboardingChecklistTotalSteps) {
        state.checklistAllStepsCompleted = true;
      }
    },
    UpdateOnboardingChecklistViewCount: (state, action: PayloadAction<number>) => {
      state.onboardingChecklistViewCount = action.payload;
    },
    UpdateChecklistCompletedStateShown: (state, action: PayloadAction<boolean>) => {
      state.checklistCompletedStateShown = action.payload;
    },
    UpdateChecklistAllStepsCompleted: (state, action: PayloadAction<boolean>) => {
      state.checklistAllStepsCompleted = action.payload;
    },
    // Records that the member confirmed a "Contact sales" request for this partner, so both the
    // partner card and the profile page keep the CTA disabled (SN-874).
    MarkPartnerSalesContacted: (state, action: PayloadAction<string>) => {
      if (!state.contactedSalesPartnerIds.includes(action.payload)) {
        state.contactedSalesPartnerIds.push(action.payload);
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPaymentMethods.pending, (state, action) => {
        state.getPaymentMethodState = 'pending';
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.getPaymentMethodState = 'idle';
        state.paymentMethods = action.payload;
        const payPalAccount = action.payload.find(
          (paymentMethod) => paymentMethod.type === PaymentMethodType.PAYPAL,
        ) as PayPalAccount;
        state.payPalAccount = payPalAccount;
        const bankAccount = action.payload.find(
          (paymentMethod) => paymentMethod.type === PaymentMethodType.BANK_ACCOUNT,
        ) as BankAccount;
        state.bankAccount = bankAccount;
        const paperCheque = action.payload.find(
          (paymentMethod) => paymentMethod.type === PaymentMethodType.PAPER_CHEQUE,
        ) as PaperCheque;
        state.paperCheque = paperCheque;
      })
      .addCase(getPaymentMethods.rejected, (state, action) => {
        state.getPaymentMethodState = 'idle';
      })
      .addCase(setPaymentMethod.pending, (state, action) => {
        state.setPaymentMethodstate = 'pending';
      })
      .addCase(setPaymentMethod.fulfilled, (state, action) => {
        state.setPaymentMethodstate = 'idle';
      })
      .addCase(setPaymentMethod.rejected, (state, action) => {
        state.setPaymentMethodstate = 'idle';
      })
      .addCase(getAllActivePartners.pending, (state, action) => {
        state.getAllActivePartnersState = 'pending';
      })
      .addCase(getAllActivePartners.fulfilled, (state, action) => {
        state.getAllActivePartnersState = 'idle';
        state.allActivePartners = action.payload;
      })
      .addCase(getAllActivePartners.rejected, (state, action) => {
        state.getAllActivePartnersState = 'idle';
      })
      .addCase(getRecentRebatePartners.pending, (state, action) => {
        state.getRecentRebatePartnersState = 'pending';
      })
      .addCase(getRecentRebatePartners.fulfilled, (state, action) => {
        state.getRecentRebatePartnersState = 'idle';
        state.recentRebatePartners = action.payload;
      })
      .addCase(getRecentRebatePartners.rejected, (state, action) => {
        state.getRecentRebatePartnersState = 'idle';
      })
      .addCase(verifyAndUpdatePaypalEmail.pending, (state, action) => {
        state.setPayPalAccountIdState = 'pending';
      })
      .addCase(verifyAndUpdatePaypalEmail.fulfilled, (state, action) => {
        state.setPayPalAccountIdState = 'idle';
        state.payPalAccount = action.payload;
      })
      .addCase(verifyAndUpdatePaypalEmail.rejected, (state, action) => {
        state.setPayPalAccountIdState = 'idle';
      })
      .addCase(unlinkPaymentMethod.pending, (state, action) => {
        state.unlinkPaymentMethodState = 'pending';
      })
      .addCase(unlinkPaymentMethod.fulfilled, (state, action) => {
        state.unlinkPaymentMethodState = 'idle';
        if (action.payload?.type === PaymentMethodType.BANK_ACCOUNT) {
          state.bankAccount = action.payload as BankAccount;
        } else if (action.payload?.type === PaymentMethodType.PAYPAL) {
          state.payPalAccount = action.payload as PayPalAccount;
        } else if (action.payload?.type === PaymentMethodType.PAPER_CHEQUE) {
          state.paperCheque = action.payload as PaperCheque;
        }
      })
      .addCase(unlinkPaymentMethod.rejected, (state, action) => {
        state.unlinkPaymentMethodState = 'idle';
      })
      .addCase(setLennoxMemberKey.pending, (state, action) => {
        state.setLennoxMemberKeyState = 'pending';
      })
      .addCase(setLennoxMemberKey.fulfilled, (state, action) => {
        state.setLennoxMemberKeyState = 'idle';
        state.lennoxMemberRelation = action.payload;
      })
      .addCase(setLennoxMemberKey.rejected, (state, action) => {
        state.setLennoxMemberKeyState = 'idle';
      })
      .addCase(getLennoxMemberRelation.pending, (state, action) => {
        state.getLennoxMemberRelationState = 'pending';
      })
      .addCase(getLennoxMemberRelation.fulfilled, (state, action) => {
        state.getLennoxMemberRelationState = 'idle';
        state.lennoxMemberRelation = action.payload;
      })
      .addCase(getLennoxMemberRelation.rejected, (state, action) => {
        state.getLennoxMemberRelationState = 'idle';
      })
      .addCase(getHomeDepotMemberRelation.pending, (state, action) => {
        state.getHomeDepotMemberRelationState = 'pending';
      })
      .addCase(getHomeDepotMemberRelation.fulfilled, (state, action) => {
        state.getHomeDepotMemberRelationState = 'idle';
        state.homeDepotMemberRelation = action.payload;
      })
      .addCase(getHomeDepotMemberRelation.rejected, (state, action) => {
        state.getHomeDepotMemberRelationState = 'idle';
      })
      .addCase(getCompanyPartnerRelations.pending, (state, action) => {
        state.getCompanyPartnerRelationsState = 'pending';
      })
      .addCase(getCompanyPartnerRelations.fulfilled, (state, action) => {
        state.companyPartnerRelations = action.payload;
        state.getCompanyPartnerRelationsState = 'idle';
      })
      .addCase(getCompanyPartnerRelations.rejected, (state, action) => {
        state.getCompanyPartnerRelationsState = 'idle';
      })
      .addCase(updateCompanyPartnerRelation.pending, (state, action) => {
        state.updateCompanyPartnerRelationState = 'pending';
      })
      .addCase(updateCompanyPartnerRelation.fulfilled, (state, action) => {
        state.updateCompanyPartnerRelationState = 'idle';
        // Array.isArray, not a truthiness check: persisted state can hold a non-array here
        // (SN-936), and findIndex/push on it would throw inside the reducer.
        if (!Array.isArray(state.companyPartnerRelations)) {
          state.companyPartnerRelations = [action.payload];
        } else {
          const indx = state.companyPartnerRelations.findIndex(
            (relation) => relation.id === action.payload.id,
          );
          if (indx !== -1) {
            state.companyPartnerRelations[indx] = action.payload;
          } else {
            state.companyPartnerRelations.push(action.payload);
          }
        }
      })
      .addCase(updateCompanyPartnerRelation.rejected, (state, action) => {
        state.updateCompanyPartnerRelationState = 'idle';
      })
      .addCase(enrollCompanyToPartner.pending, (state) => {
        state.updateCompanyPartnerRelationState = 'pending';
      })
      .addCase(enrollCompanyToPartner.fulfilled, (state, action) => {
        state.updateCompanyPartnerRelationState = 'idle';
        // See the Array.isArray note in updateCompanyPartnerRelation.fulfilled (SN-936).
        const existingRelations = Array.isArray(state.companyPartnerRelations) ?
          state.companyPartnerRelations :
          undefined;
        const relationIndex = existingRelations?.findIndex(
          (relation) => relation.partnerId === action.payload.partnerId,
        ) ?? -1;
        if (relationIndex === -1) {
          state.companyPartnerRelations = [
            ...(existingRelations ?? []),
            action.payload,
          ];
        } else if (state.companyPartnerRelations) {
          state.companyPartnerRelations[relationIndex] = {
            ...state.companyPartnerRelations[relationIndex],
            ...action.payload,
          };
        }
      })
      .addCase(enrollCompanyToPartner.rejected, (state) => {
        state.updateCompanyPartnerRelationState = 'idle';
      })
      .addCase(unlinkCompanyFromPartner.pending, (state) => {
        state.updateCompanyPartnerRelationState = 'pending';
      })
      .addCase(unlinkCompanyFromPartner.fulfilled, (state, action) => {
        state.updateCompanyPartnerRelationState = 'idle';
        // See the Array.isArray note in updateCompanyPartnerRelation.fulfilled (SN-936).
        const relationIndex = (Array.isArray(state.companyPartnerRelations) ?
          state.companyPartnerRelations :
          undefined)?.findIndex(
          (relation) => relation.partnerId === action.payload.partnerId,
        ) ?? -1;
        if (relationIndex !== -1 && state.companyPartnerRelations) {
          state.companyPartnerRelations[relationIndex] = {
            ...state.companyPartnerRelations[relationIndex],
            ...action.payload,
          };
        }
      })
      .addCase(unlinkCompanyFromPartner.rejected, (state) => {
        state.updateCompanyPartnerRelationState = 'idle';
      })
      .addCase(getSelectedPartnersForMember.pending, (state, action) => {
        state.getSelectedPartnersForMemberState = 'pending';
      })
      .addCase(getSelectedPartnersForMember.fulfilled, (state, action) => {
        state.getSelectedPartnersForMemberState = 'idle';
        state.selectedPartners = action.payload;
      })
      .addCase(getSelectedPartnersForMember.rejected, (state, action) => {
        state.getSelectedPartnersForMemberState = 'idle';
      })
      .addCase(updateMemberToPartner.pending, (state, action) => {
        state.updateMemberToPartnerState = 'pending';
      })
      .addCase(updateMemberToPartner.fulfilled, (state, action) => {
        state.updateMemberToPartnerState = 'idle';
        state.homeDepotMemberRelation = action.payload;
      })
      .addCase(updateMemberToPartner.rejected, (state, action) => {
        state.updateMemberToPartnerState = 'idle';
      })
      .addCase(updateCompanyPartner.pending, (state, action) => {
        state.updateCompanyPartnerState = 'pending';
      })
      .addCase(updateCompanyPartner.fulfilled, (state, action) => {
        state.updateCompanyPartnerState = 'idle';
      })
      .addCase(updateCompanyPartner.rejected, (state, action) => {
        state.updateCompanyPartnerState = 'idle';
      })
      .addCase(getYearlyPayments.pending, (state, action) => {
        state.getYearlyPaymentsState = 'pending';
      })
      .addCase(getYearlyPayments.fulfilled, (state, action) => {
        state.getYearlyPaymentsState = 'idle';
        state.yearlyPayments = action.payload;
      })
      .addCase(getYearlyPayments.rejected, (state, action) => {
        state.getYearlyPaymentsState = 'idle';
      })
      .addCase(getMemberConfig.pending, (state, action) => {
        state.getMemberConfigState = 'pending';
      })
      .addCase(getMemberConfig.fulfilled, (state, action) => {
        state.memberConfig = action.payload;
        state.getMemberConfigState = 'idle';
      })
      .addCase(getMemberConfig.rejected, (state, action) => {
        state.getMemberConfigState = 'idle';
      })
      .addCase(getHDSupplyMemberRelation.pending, (state, action) => {
        state.getHDSupplyMemberRelationState = 'pending';
      })
      .addCase(getHDSupplyMemberRelation.fulfilled, (state, action) => {
        state.getHDSupplyMemberRelationState = 'idle';
        state.hdSupplyMemberRelation = action.payload;
      })
      .addCase(getHDSupplyMemberRelation.rejected, (state, action) => {
        state.getHDSupplyMemberRelationState = 'idle';
      })
      .addCase(getCompanyWallet.pending, (state, action) => {
        state.getCompanyWalletState = 'pending';
      })
      .addCase(getCompanyWallet.rejected, (state, action) => {
        state.getCompanyWalletState = 'idle';
      })
      .addCase(getCompanyWallet.fulfilled, (state, action) => {
        state.getCompanyWalletState = 'idle';
        state.companyWallet = action.payload;
      })
      // Rewards Wallet
      .addCase(getCompanyRewardsWallet.pending, (state) => {
        state.getCompanyRewardsWalletState = 'pending';
      })
      .addCase(getCompanyRewardsWallet.fulfilled, (state, action) => {
        state.getCompanyRewardsWalletState = 'idle';
        const { balance, totalRewards, transactions } = action.payload;

        state.companyRewardsWallet = {
          balance,
          totalRewards,
          transactions,
        };
      })
      .addCase(getCompanyRewardsWallet.rejected, (state) => {
        state.getCompanyRewardsWalletState = 'idle';
      })
      // Payouts Wallet
      .addCase(getCompanyPayoutsWallet.pending, (state) => {
        state.getCompanyPayoutsWalletState = 'pending';
      })
      .addCase(getCompanyPayoutsWallet.fulfilled, (state, action) => {
        state.getCompanyPayoutsWalletState = 'idle';
        const { totalPayouts, transactions } = action.payload;

        state.companyPayoutsWallet = {
          totalPayouts,
          transactions,
        };
      })
      .addCase(getCompanyPayoutsWallet.rejected, (state) => {
        state.getCompanyPayoutsWalletState = 'idle';
      })
      .addCase(activatePaymentMethod.pending, (state, action) => {
        state.activatePaymentMethodstate = 'pending';
      })
      .addCase(activatePaymentMethod.fulfilled, (state, action) => {
        state.activatePaymentMethodstate = 'idle';
      })
      .addCase(activatePaymentMethod.rejected, (state, action) => {
        state.activatePaymentMethodstate = 'idle';
      })
      .addCase(getPartnerProfileConfig.pending, (state, action) => {
        state.getPartnerProfileConfigState = 'pending';
      })
      .addCase(getPartnerProfileConfig.rejected, (state, action) => {
        state.getPartnerProfileConfigState = 'idle';
      })
      .addCase(getPartnerProfileConfig.fulfilled, (state, action) => {
        state.getPartnerProfileConfigState = 'idle';
        state.partnerProfileConfig = action.payload;
      })
      .addCase(getAllCategories.pending, (state, action) => {
        state.getCategoriesState = 'pending';
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.getCategoriesState = 'idle';
        state.categories = action.payload;
      })
      .addCase(getAllCategories.rejected, (state, action) => {
        state.getCategoriesState = 'idle';
      })
      .addCase(getCompanyPartnerOptin.pending, (state) => {
        state.getCompanyPartnerOptinState = 'pending';
      })
      .addCase(getCompanyPartnerOptin.fulfilled, (state, action) => {
        state.getCompanyPartnerOptinState = 'idle';
        state.companyPartnerOptin = action.payload;
      })
      .addCase(getCompanyPartnerOptin.rejected, (state) => {
        state.getCompanyPartnerOptinState = 'error';
      })
      .addCase(createCompanyPartnerOptin.pending, (state) => {
        state.createCompanyPartnerOptinState = 'pending';
      })
      .addCase(createCompanyPartnerOptin.fulfilled, (state, action) => {
        state.createCompanyPartnerOptinState = 'idle';
        state.companyPartnerOptin = action.payload;
      })
      .addCase(createCompanyPartnerOptin.rejected, (state) => {
        state.createCompanyPartnerOptinState = 'error';
      })
      .addCase(getPartnersByCategories.pending, (state, action) => {
        state.getPartnersByCategoriesState = 'pending';
      })
      .addCase(getPartnersByCategories.fulfilled, (state, action) => {
        state.getPartnersByCategoriesState = 'idle';
        state.partnersByCategories = action.payload;
      })
      .addCase(getPartnersByCategories.rejected, (state, action) => {
        state.getPartnersByCategoriesState = 'idle';
      });
  },
});

export const {
  setMyAccountRenderIndex,
  UpdateHDSetupFlowStepsCompletionState,
  resetHomeDepotMemberRelation,
  resetCompanyPartnerOptin,
  UpdateHDSupplyEnrollmentState,
  UpdateMemberPaypalAuthorizationToken,
  UpdateShowDashboardToolbar,
  UpdateBankAccountState,
  UpdatePaperChequeState,
  UpdatePayPalSetAsDefault,
  UpdateChecklistCompletedSteps,
  UpdateOnboardingChecklistViewCount,
  UpdateChecklistCompletedStateShown,
  UpdateChecklistAllStepsCompleted,
  MarkPartnerSalesContacted,
} = DashboardSlice.actions;

export const selectPayPalAccount =
  (state: RootState) => state.dashboard.payPalAccount;
export const selectPaymentMethods =
  (state: RootState) => state.dashboard.paymentMethods;
export const selectBankAccount =
  (state: RootState) => state.dashboard.bankAccount;
export const selectPaperCheque =
  (state: RootState) => state.dashboard.paperCheque;
export const selectGetPaymentMethodState =
  (state: RootState) => state.dashboard.getPaymentMethodState;
export const selectSetPayPalAccountIdState =
  (state: RootState) => state.dashboard.setPayPalAccountIdState;

export const selectLennoxMemberRelation =
  (state: RootState) => state.dashboard.lennoxMemberRelation;
export const selectHomeDepotMemberRelation =
  (state: RootState) => state.dashboard.homeDepotMemberRelation;
export const selectGetLennoxMemberRelationState =
  (state: RootState) => state.dashboard.getLennoxMemberRelationState;
export const selectGetHomeDepotMemberRelationState =
  (state: RootState) => state.dashboard.getHomeDepotMemberRelationState;
export const selectSetLennoxMemberKeyState =
  (state: RootState) => state.dashboard.setLennoxMemberKeyState;
export const selectGetSelectedPartnersForMemberState =
  (state: RootState) => state.dashboard.getSelectedPartnersForMemberState;
export const selectGetYearlyPaymentsState =
  (state: RootState) => state.dashboard.getYearlyPaymentsState;
export const selectSelectedPartners =
  (state: RootState) => state.dashboard.selectedPartners;
export const selectMyAccountRenderIndex =
  (state: RootState) => state.dashboard.myAccountRenderIndex;
export const selectUpdateMemberToPartnerState =
  (state: RootState) => state.dashboard.updateMemberToPartnerState;
export const selectUpdateCompanyPartnerState =
  (state: RootState) => state.dashboard.updateCompanyPartnerState;
export const selectHDSetupFlowStepsCompletionState =
  (state: RootState) => state.dashboard.HDSetupFlowStepsCompletionState;
export const selectGetMemberConfigState =
  (state: RootState) => state.dashboard.getMemberConfigState;
export const selectMemberConfig =
  (state: RootState) => state.dashboard.memberConfig;
export const selectHDSupplyEnrollmentState =
  (state: RootState) => state.dashboard.HDSupplyEnrollmentState;
export const selectHDSupplyMemberRelation =
  (state: RootState) => state.dashboard.hdSupplyMemberRelation;
export const selectGetHDSupplyMemberRelationState =
  (state: RootState) => state.dashboard.getHDSupplyMemberRelationState;
export const selectMemberPaypalAuthorizationToken =
  (state: RootState) => state.dashboard.memberPaypalAuthorizationToken;
// Returns undefined (not the raw value) when persisted state holds a non-array (SN-936).
// The migration and asArrayPayload prevent this in normal flow, but a bad value persisted
// at the current store version can't be cleaned up by the one-time migration, so callers
// must never see a non-array here. undefined keeps the reference stable across renders
// since this selector is not memoized.
export const selectGetCompanyPartnerRelations =
  (state: RootState) => (Array.isArray(state.dashboard.companyPartnerRelations) ?
    state.dashboard.companyPartnerRelations :
    undefined);
export const selectShowDashboardToolbar =
  (state: RootState) => state.dashboard.showDashboardToolbar;
export const selectCompanyWallet =
  (state: RootState) => state.dashboard.companyWallet;
export const selectGetCompanyWalletState =
  (state: RootState) => state.dashboard.getCompanyWalletState;
export const selectGetCompanyRewardsWalletState =
  (state: RootState) => state.dashboard.getCompanyRewardsWalletState;
export const selectGetCompanyPayoutsWalletState =
  (state: RootState) => state.dashboard.getCompanyPayoutsWalletState;
export const selectCompanyRewardsWallet =
  (state: RootState) => state.dashboard.companyRewardsWallet;
export const selectCompanyPayoutsWallet =
  (state: RootState) => state.dashboard.companyPayoutsWallet;
export const selectGetCompanyPartnerRelationsState =
  (state: RootState) => state.dashboard.getCompanyPartnerRelationsState;
export const selectUnlinkPaymentMethodState =
  (state: RootState) => state.dashboard.unlinkPaymentMethodState;
export const selectSetPaymentMethodstate =
  (state: RootState) => state.dashboard.setPaymentMethodstate;
export const selectActivatePaymentMethodstate =
  (state: RootState) => state.dashboard.activatePaymentMethodstate;
export const selectPayPalSetAsDefault =
  (state: RootState) => state.dashboard.payPalSetAsDefault;
export const selectChecklistCompletedSteps =
  (state: RootState) => state.dashboard.checklistCompletedSteps;
export const selectChecklistAllStepsCompleted =
  (state: RootState) => state.dashboard.checklistAllStepsCompleted;
export const selectOnboardingChecklistViewCount =
  (state: RootState) => state.dashboard.onboardingChecklistViewCount;
export const selectChecklistCompletedStateShown =
  (state: RootState) => state.dashboard.checklistCompletedStateShown;
export const selectContactedSalesPartnerIds =
  (state: RootState) => state.dashboard.contactedSalesPartnerIds;
// Same defensive guard as selectGetCompanyPartnerRelations (SN-936): a non-array value
// here caused (activePartners ?? []).map(...) to throw in usePartnerConfigList, blanking
// the Partners page in the same way.
export const selectAllActivePartners =
  (state: RootState) => (Array.isArray(state.dashboard.allActivePartners) ?
    state.dashboard.allActivePartners :
    undefined);
export const selectGetAllActivePartnersState =
  (state: RootState) => state.dashboard.getAllActivePartnersState;
export const selectRecentRebatePartners =
  (state: RootState) => state.dashboard.recentRebatePartners;
export const selectGetRecentRebatePartnersState =
  (state: RootState) => state.dashboard.getRecentRebatePartnersState;
export const selectPartnerProfileConfig =
  (state: RootState) => state.dashboard.partnerProfileConfig;
export const selectGetPartnerProfileConfigState =
  (state: RootState) => state.dashboard.getPartnerProfileConfigState;
export const selectUpdateCompanyPartnerRelationState =
  (state: RootState) => state.dashboard.updateCompanyPartnerRelationState;
export const selectCategories =
  (state: RootState) => state.dashboard.categories;
export const selectGetCategoriesState =
  (state: RootState) => state.dashboard.getCategoriesState;
export const selectPartnersByCategories =
  (state: RootState) => state.dashboard.partnersByCategories;
export const selectGetPartnersByCategoriesState =
  (state: RootState) => state.dashboard.getPartnersByCategoriesState;
export const selectCompanyPartnerOptin =
  (state: RootState) => state.dashboard.companyPartnerOptin;
export const selectGetCompanyPartnerOptinState =
  (state: RootState) => state.dashboard.getCompanyPartnerOptinState;
export const selectCreateCompanyPartnerOptinState =
  (state: RootState) => state.dashboard.createCompanyPartnerOptinState;
export default DashboardSlice.reducer;
